import type { Minor, Quantity } from './money'
import {
  compareQuantity,
  isNegativeQuantity,
  isZeroQuantity,
  subQuantity,
  sumQuantity,
} from './money'

/**
 * How the household wants its private holdings counted and discounted. The key
 * is named after what it governs rather than after any one holding: a setting
 * called after a company reads as a property of the product, and the second
 * household to open this would find somebody else's company in its settings.
 */
export const PRIVATE_HOLDINGS_SETTING_KEY = 'privateHoldings'

/**
 * What sort of figure a valuation event is, which is not the same question as
 * what it is worth. A funding round, an independent appraisal, the household's
 * own restatement and a trade in the holding itself are all one price on one
 * date, and a household that can only file "a round" ends up calling a survey
 * of a flat a round because there is nowhere else to put it.
 */
export const VALUATION_KINDS = ['round', 'appraisal', 'revaluation', 'transaction'] as const

export type ValuationKind = (typeof VALUATION_KINDS)[number]

export const DEFAULT_VALUATION_KIND: ValuationKind = 'round'

/**
 * What one unit of a holding is called. Shares are only one answer: a flat, a
 * painting and a stake in a friend's company are each one indivisible thing,
 * and a dashboard that says "1 share" of a painting is stating something the
 * household never claimed.
 *
 * Null means nobody said, and nobody saying is not the same as shares — every
 * row written before holdings could be named carries that null, and only the
 * ones with a share class behind them were given the word.
 */
export const DEFAULT_UNIT_LABEL = 'unit'

export function unitLabelOf(label: string | null | undefined): string {
  const trimmed = label?.trim()
  return trimmed === undefined || trimmed === '' ? DEFAULT_UNIT_LABEL : trimmed
}

/** A holding of exactly one unit is a thing rather than a quantity of things. */
export function isIndivisible(units: Quantity): boolean {
  return compareQuantity(units, '1') === 0
}

const DANISH: ReadonlyArray<readonly [RegExp, string]> = [
  [/[æÆ]/g, 'AE'],
  [/[øØ]/g, 'OE'],
  [/[åÅ]/g, 'AA'],
]

const MAX_SYMBOL_LENGTH = 24

/**
 * A symbol for a holding that has no ticker, because nobody issues one for a
 * flat. It is derived from the name so the household never has to invent a code,
 * and it is only an identifier: the name is what is displayed.
 */
export function slugSymbol(name: string): string {
  let slug = name
  for (const [pattern, replacement] of DANISH) slug = slug.replace(pattern, replacement)
  slug = slug
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SYMBOL_LENGTH)
    .replace(/-+$/g, '')

  return slug === '' ? 'HOLDING' : slug
}

/**
 * The first spelling of `candidate` nothing has taken yet. Two holdings a
 * household happens to name the same thing are two holdings; resolving them onto
 * one symbol would merge their valuations and their units into a single
 * position, which is a loss of money rather than a naming collision.
 */
export function distinctFrom(candidate: string, taken: ReadonlySet<string>): string {
  if (!taken.has(candidate)) return candidate
  for (let suffix = 2; ; suffix += 1) {
    const next = `${candidate}-${suffix}`
    if (!taken.has(next)) return next
  }
}

/** The manual account a private holding is held in, keyed off its symbol. */
export function holdingStableKey(symbol: string): string {
  return `private-${symbol.toLowerCase()}`
}

/**
 * Units that left a holding on a date, and what came back for them.
 *
 * It is not a restatement of the count and the two must never be read as one
 * act. A restatement says the figure on file was wrong and replaces it for every
 * date, which is right when somebody mistyped it. A disposal says the figure was
 * right until this day and smaller after it, so the dates before it keep the
 * count they actually had — the founder who sold a slice at a round held the
 * whole of it for the years before, and a product that can only restate would
 * have to choose which half of that history to state wrongly.
 */
export interface Disposal {
  date: string
  units: Quantity
  /**
   * What the units fetched, in the holding's own currency. Absent where nobody
   * said, which is not the same claim as nothing: a holding whose value drops
   * with no sum beside it leaves the reader to guess where the money went.
   */
  proceedsMinor?: Minor
  note?: string
}

/** Units gone by the end of `date`, which is nothing at all before the first one. */
export function disposedBy(disposals: readonly Disposal[], date: string): Quantity {
  return sumQuantity(disposals.filter((d) => d.date <= date).map((d) => d.units))
}

/** Units gone in the end, whenever they went. */
export function totalDisposed(disposals: readonly Disposal[]): Quantity {
  return sumQuantity(disposals.map((d) => d.units))
}

/**
 * How many units are held on a date: what was stated, less everything that has
 * left by then. Later disposals are not subtracted, which is the whole of what
 * separates this from restating the count.
 */
export function unitsHeldOn(
  stated: Quantity,
  disposals: readonly Disposal[],
  date: string,
): Quantity {
  if (disposals.length === 0) return stated
  return subQuantity(stated, disposedBy(disposals, date))
}

/**
 * What a holding has to have been stated as for `net` units to be held on a
 * date, given what has already left it.
 *
 * Somebody entering a count is saying what they hold on the day they are
 * entering it, and where units have already gone that is not the count the
 * holding began with. Subtracting the disposals a second time is the arithmetic
 * error this exists to make impossible.
 */
export function statedFromHeldOn(
  net: Quantity,
  disposals: readonly Disposal[],
  date: string,
): Quantity {
  if (disposals.length === 0) return net
  return sumQuantity([net, disposedBy(disposals, date)])
}

export interface DisposalRefusal {
  /** Which field the household would have to change, for a form to say where. */
  field: 'date' | 'units'
  message: string
}

/**
 * Why a disposal cannot be recorded, in the words of whoever would refuse it.
 *
 * A holding that goes negative, or one that loses units on a day it was not yet
 * held, is not a holding anybody can read — and the figures downstream would go
 * on multiplying it by a price rather than failing.
 */
export function refuseDisposal(
  stated: Quantity,
  existing: readonly Disposal[],
  candidate: Disposal,
  heldSince?: string,
): DisposalRefusal | undefined {
  if (isZeroQuantity(candidate.units) || isNegativeQuantity(candidate.units)) {
    return { field: 'units', message: 'a disposal is some units leaving, so it needs more than none' }
  }
  if (heldSince !== undefined && candidate.date < heldSince) {
    return {
      field: 'date',
      message: `the holding was not held on ${candidate.date}; it begins on ${heldSince}`,
    }
  }
  if (existing.some((d) => d.date === candidate.date)) {
    return {
      field: 'date',
      message: `${candidate.date} already has a disposal; remove it before recording another`,
    }
  }

  const remaining = subQuantity(stated, totalDisposed([...existing, candidate]))
  if (isNegativeQuantity(remaining)) {
    const held = subQuantity(stated, totalDisposed(existing))
    return {
      field: 'units',
      message: `only ${held} of ${stated} are left, so ${candidate.units} cannot leave`,
    }
  }
  return undefined
}

/** Newest first, which is the order a history is read in. */
export function sortedDisposals(disposals: readonly Disposal[]): Disposal[] {
  return [...disposals].sort((a, b) => b.date.localeCompare(a.date))
}
